import { cn } from '@broccoli/ui';

import { errorMessage } from './error-copy';

import type { ReactNode } from 'react';

/**
 * The handful of shapes every Broccoli screen is made of. They live here rather than in
 * `packages/ui` because they are layout for this app, not a design-system contract — the
 * package holds the pieces a second surface would need.
 */

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]',
        padded && 'p-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="text-[15px] font-semibold text-[var(--color-text)]">{children}</h2>
      {action}
    </div>
  );
}

type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<Tone, string> = {
  neutral: 'bg-[var(--color-surface-sunken)] text-[var(--color-text-muted)]',
  primary: 'bg-[var(--color-primary-50)] text-[var(--color-primary-700)]',
  success: 'bg-[var(--color-success-surface)] text-[var(--color-primary-700)]',
  warning: 'bg-[var(--color-warning-surface)] text-[var(--color-warning)]',
  danger: 'bg-[var(--color-danger-surface)] text-[var(--color-danger)]',
  info: 'bg-[var(--color-info-surface)] text-[var(--color-info)]',
};

export function Chip({
  children,
  tone = 'neutral',
  className,
  title,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Avatar({ name, size = 26 }: { name: string; size?: number }) {
  const text = name.trim().slice(0, 2) || '?';
  return (
    <span
      title={name}
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-100)] font-semibold text-[var(--color-primary-700)]"
    >
      {text}
    </span>
  );
}

/**
 * Spec 12's rule, made reusable: an empty state is never a blank panel. It says what would
 * be here and offers the action that would put something in it.
 */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[var(--color-border-strong)] px-6 py-10 text-center">
      <p className="text-[15px] font-medium text-[var(--color-text)]">{title}</p>
      {hint ? <p className="max-w-md text-[13px] text-[var(--color-text-muted)]">{hint}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-[var(--color-surface-hover)]', className)}
      aria-hidden
    />
  );
}

/**
 * An error the user can act on. The Thai copy lives in `error-copy.ts` — one map, so a new API
 * code cannot quietly reach a Thai user as English prose (defect #12).
 */
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;

  return (
    <p
      role="alert"
      className="rounded-lg bg-[var(--color-danger-surface)] px-3 py-2 text-[13px] text-[var(--color-danger)]"
    >
      {errorMessage(error)}
    </p>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] text-[var(--color-text-muted)]">{label}</span>
      <span className="text-[14px] text-[var(--color-text)]">{children}</span>
    </div>
  );
}

export const inputClass =
  'w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[14px] outline-none focus:border-[var(--color-border-focus)]';
